/**
 * Shared fuzzy matching utilities for org-board filter components.
 *
 * Single source of truth for text matching logic used across:
 * - org-hats-list-dialog
 * - org-positions-list-dialog
 * - org-new-hat-dialog
 * - org-search-bar component
 */

/** Compute Levenshtein (edit) distance between two strings. Space-optimized 1D DP. */
export function levenshtein(a: string, b: string): number {
    const m = a.length, n = b.length;
    const dp: number[] = Array.from({ length: n + 1 }, (_, i) => i);
    for (let i = 1; i <= m; i++) {
        let prev = dp[0];
        dp[0] = i;
        for (let j = 1; j <= n; j++) {
            const temp = dp[j];
            dp[j] = a[i - 1] === b[j - 1] ? prev : 1 + Math.min(prev, dp[j], dp[j - 1]);
            prev = temp;
        }
    }
    return dp[n];
}

/**
 * Fuzzy match a single word against a search word using Levenshtein distance.
 * Allows typo tolerance: 0 edits for 1-2 char words, floor(len/3) for longer.
 * Also checks prefix matching for partial input.
 */
export function fuzzyMatchWord(textWord: string, searchWord: string): boolean {
    if (textWord.includes(searchWord)) return true;
    const maxDist = searchWord.length <= 2 ? 0 : Math.floor(searchWord.length / 3);
    if (levenshtein(textWord, searchWord) <= maxDist) return true;
    if (textWord.length >= searchWord.length) {
        const prefix = textWord.substring(0, searchWord.length + 1);
        if (levenshtein(prefix, searchWord) <= maxDist) return true;
    }
    return false;
}

/**
 * Fuzzy match text against a search term. Handles multi-word queries:
 * - Single word: any word in text must fuzzy-match
 * - Multi word: ALL search words must match some word in text
 * Falls back to substring match first for speed.
 */
export function fuzzyMatch(text: string, searchTerm: string): boolean {
    if (text.includes(searchTerm)) return true;

    const textWords = text.split(/\s+/);
    const searchWords = searchTerm.split(/\s+/).filter(w => w.length > 0);

    if (searchWords.length > 1) {
        return searchWords.every(sw => textWords.some(tw => fuzzyMatchWord(tw, sw)));
    }

    return textWords.some(tw => fuzzyMatchWord(tw, searchTerm));
}

/** Minimal org-part shape needed for subtree expansion. */
export interface FilterOrgPartLike {
    partKey?: string;
    name?: string;
    parentPartKey?: string;
    partType?: string;
}

/**
 * Post names under any org part whose name fuzzy-matches the term (subtree
 * expansion). Lets a CONTAINER term ("Department A1" — e.g. the org-board
 * context menu pre-fills the right-clicked part's name) filter staff by the
 * posts inside it: users hold POSTS, so without expansion a container term
 * can never match anyone.
 */
export function expandTermToSubtreePostNames(parts: FilterOrgPartLike[], term: string): Set<string> {
    const matchedRoots = parts.filter(p => !!p.name && fuzzyMatch(p.name, term));
    const out = new Set<string>();
    if (!matchedRoots.length) return out;

    const childrenByParent = new Map<string, FilterOrgPartLike[]>();
    for (const p of parts) {
        const key = p.parentPartKey || '';
        if (!childrenByParent.has(key)) childrenByParent.set(key, []);
        childrenByParent.get(key)?.push(p);
    }
    const stack = [...matchedRoots];
    for (let cur = stack.pop(); cur; cur = stack.pop()) {
        if (cur.partType === 'post' && cur.name) out.add(cur.name);
        if (cur.partKey) stack.push(...(childrenByParent.get(cur.partKey) ?? []));
    }
    return out;
}
